/**
 * Evidence and Gap Types
 */

import type { ItemEvaluation } from './assessment';

// Evidence file from Azure Blob Storage
export interface EvidenceFile {
  id: string;
  name: string;
  original_name: string;
  size: number;
  mime_type: string;
  blob_path: string;
  download_url: string;
  uploaded_at: string;
}

export type EvidenceType = 'document' | 'screenshot' | 'policy' | 'procedure' | 'other';

export interface EvidenceTypeOption {
  value: EvidenceType;
  label: string;
  description: string;
}

/** Evidence type choices shared by the upload form and the draft editor. */
export const EVIDENCE_TYPE_OPTIONS: EvidenceTypeOption[] = [
  { value: 'document', label: 'Document', description: 'General business document' },
  { value: 'screenshot', label: 'Screenshot', description: 'Screen capture or image' },
  { value: 'policy', label: 'Policy', description: 'Company policy document' },
  { value: 'procedure', label: 'Procedure', description: 'Process or procedure document' },
  { value: 'other', label: 'Other', description: 'Other type of evidence' }
];

/**
 * Status of one evidence record. `draft` is Draft Evidence: uploaded by the
 * assigned Assessor and kept out of review until a CM/CO submits it.
 */
export type EvidenceRecordStatus = 'draft' | 'pending' | 'approved' | 'rejected';

// Evidence types
export interface Evidence {
  id: number;
  assessment_item_id: number;
  evidence_type: EvidenceType;
  evidence_name: string;
  evidence_description: string;
  notes?: string;
  status: EvidenceRecordStatus;
  review_notes?: string;
  remediation_date?: string;
  uploaded_by_id: number;
  uploaded_by_name?: string;
  // Null while the evidence is a draft
  submitted_by_id: number | null;
  submitted_by_name: string | null;
  submitted_at: string | null;
  reviewed_by_id?: number;
  reviewed_by_name?: string;
  created_at: string;
  updated_at: string;
  reviewed_at?: string;
  files: EvidenceFile[];
}

/** Aggregated evidence status of an assessment item (see deriveUploadEvidenceStatus). */
export type EvidenceStatus = 'none' | 'draft' | 'pending' | 'approved' | 'rejected' | 'mixed';

/** Which Draft Evidence actions the current user gets (see getDraftEvidencePermissions). */
export interface DraftEvidencePermissions {
  canSubmit: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

export interface DraftEvidenceActionCallbacks {
  onSettled?: () => void;
}

/** Return type of useDraftEvidenceActions. */
export interface DraftEvidenceActionsHandle {
  submitDraft: (assessmentItemId: number, callbacks?: DraftEvidenceActionCallbacks) => void;
  deleteDraft: (assessmentItemId: number, callbacks?: DraftEvidenceActionCallbacks) => void;
  isSubmitting: boolean;
  isDeleting: boolean;
}

export type RiskLevel = 'High' | 'Medium' | 'Low' | 'Unknown';

// Evidence statistics types
export interface EvidenceStatistics {
  needsEvidence: number;
  pending: number;
  approved: number;
  rejected: number;
  mixed: number;
  awaitingSubmission: number;
}

export interface AssessorEvidenceStatistics {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

// Contract Role Dashboard types
export interface GapItem {
  id: number;
  assessment_id: number;
  assessment_name: string;
  process_id: number;
  status: string;
  notes: string;
  contractId: number;
  contractNumber: string;
  contractName: string;
  supplierName: string;
  assessmentId: number;
  assessmentName: string;
  evidenceCount: number;
  draftCount: number;
  pendingCount: number;
  approvedCount: number;
  rejectedCount: number;
  evidenceStatus: EvidenceStatus;
  hasEvidence: boolean;
  lastReviewNotes?: string;
  // Additional fields from backend
  process_name?: string;
  process_description?: string;
  process_priority?: 'High' | 'Medium' | 'Low';
  domain_name?: string;
  created_at?: string;
}

export interface EvidenceForReview {
  id: number;
  evidence_name: string;
  evidence_description: string;
  evidence_type: EvidenceType;
  status: EvidenceRecordStatus;
  notes: string;
  uploaded_by_id: number;
  uploaded_by_name: string;
  submitted_by_id: number | null;
  submitted_by_name: string | null;
  submitted_at: string | null;
  created_at: string;
  reviewed_at?: string;
  reviewed_by_id?: number;
  review_notes?: string;
  // Assessment item context
  assessment_item_id: number;
  process_name: string;
  process_priority: 'High' | 'Medium' | 'Low';
  process_evaluation?: ItemEvaluation;
  domain_name: string;
  // Assessment context
  assessment_id: number;
  assessment_name: string;
  // Contract context
  contract_number: string;
  supplier_name: string;
  // Files from Azure Blob Storage
  files: EvidenceFile[];
}
